import { useState, useEffect, useCallback, useRef } from 'react'
import { useSsoToken } from './useSsoToken'
import { getClaimTasks, updateClaimTask } from '../services/claimsApi'
import { getApiErrorMessage } from '../utils/errorMessages'
import type { ApiSlice } from './useClaimDetails'
import type { ClaimTask, TaskHoldValues } from '../types/task'

const initialSlice = <T>(): ApiSlice<T> => ({ data: null, loading: false, error: null })
const loadingSlice = <T>(): ApiSlice<T> => ({ data: null, loading: true, error: null })

// Fetches the connected claim's open tasks for the Tasks tab (MSD-682).
export function useClaimTasks(claimNumber: string | null) {
  const { getToken } = useSsoToken()

  const [tasks, setTasks] = useState<ApiSlice<ClaimTask[]>>(initialSlice)
  const [holdingTaskId, setHoldingTaskId] = useState<string | null>(null)
  // Kept with its task id so the message shows only on the card that failed.
  const [holdError, setHoldError] = useState<{ taskId: string; message: string } | null>(null)

  // Each fetch gets an id; a response only lands if its fetch is still the latest,
  // so a late response can't clobber state after a claim swap, retry, or unmount.
  const fetchIdRef = useRef(0)

  // `silent` refreshes the list in the background: the current tasks stay on screen
  // instead of being replaced by the loading skeletons. That matters after an update,
  // because blanking the list unmounts every card and throws away its open state.
  const fetchTasks = useCallback(
    async (silent = false) => {
      const fetchId = ++fetchIdRef.current
      const isStale = () => fetchIdRef.current !== fetchId

      if (!claimNumber) {
        setTasks(initialSlice)
        return
      }

      if (!silent) setTasks(loadingSlice)

      try {
        const token = await getToken()
        const data = await getClaimTasks(claimNumber, token)
        if (!isStale()) setTasks({ data, loading: false, error: null })
      } catch (err: unknown) {
        if (isStale()) return
        console.error('[ClaimTasks] Failed to load tasks:', err)
        // A failed background refresh keeps the list that is already on screen; the
        // update itself succeeded, so wiping the tab would be the wrong story.
        if (silent) return
        setTasks({ data: null, loading: false, error: getApiErrorMessage(err, 'Failed to load tasks.') })
      }
    },
    [claimNumber, getToken]
  )

  // Takes no arguments, so it is safe to hand straight to an onClick.
  const retry = useCallback(() => fetchTasks(), [fetchTasks])

  useEffect(() => {
    fetchTasks()
    return () => {
      fetchIdRef.current += 1
    }
    // Refetch only on claim change; fetchTasks is stable (memoized on claimNumber).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimNumber])

  // Puts a task on hold in one PATCH — On Hold Until date and description travel
  // together. The backend derives the Hold status itself, so the request carries no
  // status id. Resolves true on success so the card only closes when saved.
  const holdTask = useCallback(
    async (taskId: string, values: TaskHoldValues): Promise<boolean> => {
      if (!claimNumber) return false

      const numericTaskId = Number(taskId)
      if (!Number.isInteger(numericTaskId)) {
        console.error('[ClaimTasks] Task id is not numeric, cannot update:', taskId)
        setHoldError({ taskId, message: 'This task cannot be updated.' })
        return false
      }

      setHoldingTaskId(taskId)
      setHoldError(null)

      try {
        const token = await getToken()
        await updateClaimTask(
          claimNumber,
          numericTaskId,
          {
            onHoldUntilDate: new Date(values.onHoldUntilDate),
            holdDescription: values.holdDescription,
          },
          token
        )
        // Background refresh so the list does not flash back to skeletons.
        await fetchTasks(true)
        return true
      } catch (err: unknown) {
        console.error('[ClaimTasks] Failed to put task on hold:', err)
        setHoldError({ taskId, message: getApiErrorMessage(err, 'Failed to update task.') })
        return false
      } finally {
        setHoldingTaskId(null)
      }
    },
    [claimNumber, getToken, fetchTasks]
  )

  return {
    tasks,
    retry,
    holdTask,
    holdingTaskId,
    holdError,
  }
}
